import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Gem, Award, Leaf, Heart } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/context/LanguageContext";

const HERO = "https://images.unsplash.com/photo-1779398970793-a795aa71eaf4?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";
const ATELIER = "https://images.pexels.com/photos/12074236/pexels-photo-12074236.jpeg?auto=compress&cs=tinysrgb&dpr=2&w=1200";
const CRAFT = "https://images.unsplash.com/photo-1605100804763-247f67b3557e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const values = [
  { icon: Gem, title: "Rare Materials", text: "Only the finest diamonds, gold and coloured stones, hand-selected by our gemologists." },
  { icon: Award, title: "Master Craftsmanship", text: "Each piece is sculpted by artisans with decades of experience at the bench." },
  { icon: Leaf, title: "Responsibly Sourced", text: "Conflict-free stones and recycled precious metals across every collection." },
  { icon: Heart, title: "Made to Last", text: "A lifetime warranty and complimentary care for jewelry meant for generations." },
];

export default function About() {
  const { t } = useLanguage();
  return (
    <div data-testid="about-page">
      <section className="relative h-[70vh] min-h-[460px] overflow-hidden">
        <img src={HERO} alt="M&A Jewelry" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-black/45" />
        <div className="relative h-full flex flex-col items-center justify-center text-center px-5">
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.9, delay: 0.1 }} className="font-serif text-5xl sm:text-6xl lg:text-7xl font-light text-white max-w-3xl leading-tight">
            {t("The art of enduring beauty")}
          </motion.h1>
        </div>
      </section>

      <section className="mx-auto max-w-3xl px-5 lg:px-10 py-24 text-center">
        <p className="overline text-gold mb-4">{t("Our Philosophy")}</p>
        <h2 className="font-serif text-4xl sm:text-5xl font-light leading-tight">
          {t("We craft jewelry not for a season, but for a lifetime.")}
        </h2>
        <p className="mt-8 text-muted-foreground leading-relaxed text-lg">
          {t("M&A Jewelry was founded in a small atelier, with a singular belief: that jewelry should be an heirloom, carrying meaning far beyond its material worth. Over a century later, that belief still guides every design that leaves our workshop.")}
        </p>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 pb-24 grid lg:grid-cols-2 gap-6">
        <div className="relative overflow-hidden aspect-[4/5] bg-secondary group">
          <img src={ATELIER} alt="Atelier" className="hover-zoom-img h-full w-full object-cover" />
        </div>
        <div className="relative overflow-hidden aspect-[4/5] bg-secondary group mt-0 lg:mt-16">
          <img src={CRAFT} alt="Craftsmanship" className="hover-zoom-img h-full w-full object-cover" />
        </div>
      </section>

      <section className="bg-secondary/40 py-24">
        <div className="mx-auto max-w-[1400px] px-5 lg:px-10">
          <div className="text-center mb-14">
            <p className="overline text-gold mb-3">{t("Our Promise")}</p>
            <h2 className="font-serif text-4xl sm:text-5xl font-light">{t("What sets us apart")}</h2>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((v) => (
              <div key={v.title} className="text-center">
                <div className="h-16 w-16 mx-auto border border-gold/40 grid place-items-center mb-5">
                  <v.icon className="h-7 w-7 text-gold" strokeWidth={1.2} />
                </div>
                <h3 className="font-serif text-2xl mb-2">{t(v.title)}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{t(v.text)}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-5 lg:px-10 py-24 text-center">
        <h2 className="font-serif text-4xl sm:text-5xl font-light mb-6">{t("Begin your M&A Jewelry story")}</h2>
        <p className="text-muted-foreground max-w-lg mx-auto mb-8">
          {t("Discover pieces designed to be treasured, or book a private appointment with our concierge.")}
        </p>
        <div className="flex gap-4 justify-center">
          <Link to="/shop"><Button className="rounded-none h-12 px-8" data-testid="about-shop-btn">{t("Explore Jewelry")}</Button></Link>
          <Link to="/contact"><Button variant="outline" className="rounded-none h-12 px-8" data-testid="about-contact-btn">{t("Contact Us")}</Button></Link>
        </div>
      </section>
    </div>
  );
}
